import {defineStore} from 'pinia';
import {getEntities, getAllEntities} from '@/services/entityService.js';
import {getUserEntities} from '@/services/userEntityService.js';
import {getNetworks, getAllNetworks} from '@/services/networkService.js';
import {getAllExams, getAllExamProducts} from '@/services/examService.js';
import {ENTITY_STATUSES, NETWORK_STATUSES} from "@/utils/constants.js";
import {userLogin} from "@/services/userService.js";

export const useAppStore = defineStore('appStore', {
  state: () => {
    return {
      user: {}, // This is for app DB saved attributes, if you're looking for the frontend user session, see authStore.js.
      entities: [],
      userEntities: [],
      networks: [],
      exams: [],
      examProducts: [],
      loading: true,
    };
  },
  actions: {
    async initializeStore(isAdminUser = false, isNetworkAdequacyUser = false) {
      try {
        this.loading = true;
        await Promise.all([
          this.fetchUser(),
          isAdminUser ? this.fetchAllEntities() : this.fetchEntities(),
          this.fetchUserEntities(),
          (isAdminUser || isNetworkAdequacyUser) ? this.fetchAllNetworks() : this.fetchNetworks(),
          this.fetchAllExams(),
          this.fetchAllExamProducts(),
        ]);
        this.loading = false;
      } catch (err) {
        console.error('Error initializing store:', err);
        this.loading = false;
      }
    },
    // Entities
    async fetchEntities(){
      const e = await getEntities();
      this.entities = e?.sort((a, b) => a.name.localeCompare(b.name)) ?? [];
    },
    async fetchAllEntities(){
      const e = await getAllEntities();
      this.entities = e?.sort((a, b) => a.name.localeCompare(b.name)) ?? [];
    },
    async fetchUserEntities() {
      this.userEntities = await getUserEntities();
    },
    getUserEntitiesByType(type) {
      const userEntityIds = new Set(this.userEntities.map(i => i.entityId.toString()));
      return this.entities
          .filter(entity => entity.type === type && entity.status === ENTITY_STATUSES.Active && userEntityIds.has(entity.id.toString()))
          .sort((a, b) => a.name.localeCompare(b.name));
    },
    getUserEntitiesByTypes(types) {
      const userEntityIds = new Set(this.userEntities.map(i => i.entityId.toString()));
      return this.entities
          .filter(entity => types.includes(entity.type) && entity.status === ENTITY_STATUSES.Active && userEntityIds.has(entity.id.toString()))
          .sort((a, b) => a.name.localeCompare(b.name));
    },
    getEntityNameById(id) {
      return this.entities.find(e => e.id === id)?.name;
    },
    // Networks
    async fetchNetworks() {
      const n = await getNetworks();
      this.networks = n?.sort((a, b) => a.networkName.localeCompare(b.networkName)) ?? [];
    },
    async fetchAllNetworks() {
      const n = await getAllNetworks();
      this.networks = n?.sort((a, b) => a.networkName.localeCompare(b.networkName)) ?? [];
    },
    async fetchAllExams() {
      const ex = await getAllExams();
      this.exams = ex?.sort((a, b) => a.examType.localeCompare(b.examType)) ?? [];
    },
    async fetchAllExamProducts() {
      const ep = await getAllExamProducts();
      this.examProducts = ep?.sort((a, b) => a.productName.localeCompare(b.productName)) ?? [];
    },
    getNetworksByEntityId(entityId) {
      if (!entityId) return [];

      return this.networks
          .filter(network =>
            entityId.toString() === network.entityId.toString() &&
            network.status === NETWORK_STATUSES.Active
          )
          .sort((a, b) => a.networkName.localeCompare(b.networkName));
    },
    getNetworkIdById(id) {
      return this.networks
          .find(n => n.id === id)?.networkId;
    },
    getExamProductById(id) {
      return this.examProducts
          .find(ep => ep.id === id);
    },
    getNetworksAwaitingReview() {
      return this.networks
          .filter(network =>
            network.status === NETWORK_STATUSES.Reviewing
          )
          .sort((a, b) => a.networkName.localeCompare(b.networkName));
    },
    getNetworksAll() {
      return (this.networks ?? []).sort((a, b) => a.networkName.localeCompare(b.networkName));
    },
    getNetworkNameById(id){
      return this.networks.find(n => n.id === id)?.networkName;
    },
    getEntities() {
      return this.entities.filter(e => e.status === ENTITY_STATUSES.Active).sort((a, b) => a.name.localeCompare(b.name));
    },
    getEntitiesAll() {
      return (this.entities ?? []).sort((a, b) => a.name.localeCompare(b.name));
    },
    getUserEntities() {
        const userEntityIds = new Set(this.userEntities.map(i => i.entityId.toString()));
    },
    getAllEntityExamProductOptions() {
      if (!this.entities.length || !this.exams.length || !this.examProducts.length) {
        return [];
      }

      const result = [];

      const activeEntities = this.entities.filter(
          e => e.status === ENTITY_STATUSES.Active
      );

      activeEntities.forEach(entity => {

        const phase1Exams = this.exams.filter(exam =>
            exam.entityId?.toString() === entity.id.toString() &&
            exam.phase === 'Phase1'
        );

        phase1Exams.forEach(exam => {

          const products = this.examProducts.filter(product =>
              product.examId?.toString() === exam.id.toString()
          );

          const productNames = products
              .map(product => product.productName)
              .join(', ');

          result.push({
            entityId: entity.id,
            examId: exam.id,

            examProducts: products,

            entityName: entity.name,
            examType: exam.examType,
            phase: exam.phase,

            label: `${entity.name} / ${exam.examType} • ${productNames}`
          });
        });
      });

      return result.sort((a, b) => a.label.localeCompare(b.label));
    },
    // User
    async fetchUser() {
      this.user = await userLogin();
    },
  },
});
